(() => {
  const root = window.Shopify?.routes?.root || '/';
  const liveRegion = () => document.getElementById('AvenueLiveRegion');
  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 20);
  };

  const money = (cents, currency = window.Shopify?.currency?.active || 'AUD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currency
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const focusable = (container) => [...container.querySelectorAll(
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
  )].filter((element) => !element.hidden && element.offsetParent !== null);

  class AvenueDialog {
    constructor(shell) {
      this.shell = shell;
      this.panel = shell.querySelector('[data-dialog-panel]');
      this.lastFocus = null;
      shell.querySelectorAll('[data-dialog-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      shell.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.shell.hidden = false;
      this.shell.classList.add('is-open');
      this.shell.setAttribute('aria-hidden', 'false');
      document.body.classList.add('dialog-open');
      window.setTimeout(() => focusable(this.panel)[0]?.focus(), 30);
    }

    close() {
      this.shell.classList.remove('is-open');
      this.shell.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('dialog-open');
      window.setTimeout(() => { this.shell.hidden = true; }, 280);
      this.lastFocus?.focus?.();
    }

    onKeydown(event) {
      if (event.key === 'Escape') return this.close();
      if (event.key !== 'Tab') return;
      const items = focusable(this.panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const dialogs = new Map();
  document.querySelectorAll('[data-dialog]').forEach((shell) => dialogs.set(shell.id, new AvenueDialog(shell)));
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-dialog-open]');
    if (!trigger) return;
    const dialog = dialogs.get(trigger.dataset.dialogOpen);
    if (!dialog) return;
    event.preventDefault();
    dialog.open(trigger);
    if (trigger.dataset.dialogOpen === 'CartDrawer') refreshCartDrawer();
  });

  const renderDrawerCart = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-drawer-count]').forEach((node) => {
      node.textContent = cart.item_count;
    });
    const body = document.querySelector('[data-cart-drawer-body]');
    const subtotal = document.querySelector('[data-cart-drawer-subtotal]');
    if (!body) return;
    if (!cart.items.length) {
      body.innerHTML = `<div class="empty-state"><p>${body.dataset.emptyText}</p><a class="text-link" href="${body.dataset.continueUrl}"><span aria-hidden="true">←</span> ${body.dataset.continueText}</a></div>`;
    } else {
      body.innerHTML = cart.items.map((item) => `
        <article class="drawer-cart-item" data-line="${item.key}">
          ${item.image ? `<img src="${item.image}" alt="${(item.featured_image?.alt || item.product_title).replaceAll('"', '&quot;')}" width="128" height="128">` : '<span class="drawer-cart-item__placeholder" aria-hidden="true"></span>'}
          <div class="drawer-cart-item__details">
            <a href="${item.url}">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <p class="drawer-cart-item__price">${money(item.final_line_price)}</p>
          </div>
        </article>`).join('');
    }
    if (subtotal) subtotal.textContent = money(cart.total_price);
  };

  const refreshCartDrawer = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    renderDrawerCart(await response.json());
  };

  const changeCart = async (id, quantity) => {
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity: Number(quantity) })
    });
    if (!response.ok) throw new Error('Cart update failed');
    const cart = await response.json();
    renderDrawerCart(cart);
    announce(document.querySelector('[data-cart-drawer-body]')?.dataset.updatedText || 'Cart updated');
    return cart;
  };

  document.addEventListener('click', (event) => {
    const change = event.target.closest('[data-cart-change]');
    if (!change) return;
    change.disabled = true;
    changeCart(change.dataset.cartChange, change.dataset.quantity).catch(() => window.location.assign(`${root}cart`));
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-input]');
    if (!input) return;
    changeCart(input.dataset.cartInput, input.value).catch(() => window.location.assign(`${root}cart`));
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-page-input]');
    if (!input) return;
    input.disabled = true;
    changeCart(input.dataset.cartPageInput, input.value)
      .then(() => window.location.reload())
      .catch(() => window.location.assign(`${root}cart`));
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const variants = JSON.parse(form.querySelector('[data-product-variants]')?.textContent || '[]');
    const idInput = form.querySelector('[name="id"]');
    const submit = form.querySelector('[type="submit"]');
    const submitText = form.querySelector('[data-submit-text]');
    const price = form.closest('[data-product-root]')?.querySelector('[data-product-price]');

    const updateVariant = () => {
      const optionGroups = [...form.querySelectorAll('[data-option-position]')];
      const selected = optionGroups.map((group) => group.querySelector('input:checked')?.value);
      const variant = optionGroups.length
        ? variants.find((candidate) => candidate.options.every((option, index) => option === selected[index]))
        : variants.find((candidate) => String(candidate.id) === String(idInput.value)) || variants.find((candidate) => candidate.available);
      if (!variant) {
        submit.disabled = true;
        submitText.textContent = submit.dataset.unavailableText;
        return;
      }
      idInput.value = variant.id;
      submit.disabled = !variant.available;
      submitText.textContent = variant.available ? submit.dataset.addText : submit.dataset.soldOutText;
      if (price) price.textContent = money(variant.price);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };
    form.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-value]')) updateVariant();
    });
    form.addEventListener('submit', async (event) => {
      if (form.dataset.ajax !== 'true') return;
      event.preventDefault();
      submit.disabled = true;
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
        });
        if (!response.ok) throw new Error((await response.json()).description);
        await refreshCartDrawer();
        announce(submit.dataset.addedText);
        const drawer = dialogs.get('CartDrawer');
        if (drawer) drawer.open(submit);
      } catch (error) {
        announce(error.message || submit.dataset.errorText);
      } finally {
        updateVariant();
      }
    });
    updateVariant();
  });

  document.addEventListener('click', async (event) => {
    const quickAdd = event.target.closest('[data-quick-add]');
    if (!quickAdd) return;
    quickAdd.disabled = true;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(quickAdd.dataset.quickAdd), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unable to add item');
      await refreshCartDrawer();
      announce(quickAdd.dataset.addedText);
      dialogs.get('CartDrawer')?.open(quickAdd);
    } catch (_) {
      window.location.assign(quickAdd.dataset.productUrl);
    } finally {
      quickAdd.disabled = false;
    }
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-load-more]');
    if (!button) return;
    button.disabled = true;
    button.textContent = button.dataset.loadingText;
    try {
      const response = await fetch(button.dataset.nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = page.querySelector(`#${CSS.escape(button.dataset.gridId)}`);
      const target = document.getElementById(button.dataset.gridId);
      source?.querySelectorAll('.product-card').forEach((card) => target.append(card));
      const nextButton = page.querySelector('[data-load-more]');
      if (nextButton) {
        button.dataset.nextUrl = nextButton.dataset.nextUrl;
        button.disabled = false;
        button.textContent = button.dataset.label;
      } else {
        button.closest('.load-more-wrap')?.remove();
      }
    } catch (_) {
      window.location.assign(button.dataset.nextUrl);
    }
  });

  const predictiveInput = document.querySelector('[data-predictive-input]');
  const predictiveTarget = document.querySelector('[data-predictive-results]');
  let predictiveTimer;
  predictiveInput?.addEventListener('input', () => {
    window.clearTimeout(predictiveTimer);
    const query = predictiveInput.value.trim();
    if (query.length < 2) { predictiveTarget.innerHTML = ''; return; }
    predictiveTimer = window.setTimeout(async () => {
      predictiveTarget.setAttribute('aria-busy', 'true');
      const url = `${root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;
      const response = await fetch(url);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      predictiveTarget.innerHTML = page.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
      predictiveTarget.setAttribute('aria-busy', 'false');
    }, 220);
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const thumbTrack = gallery.querySelector('.product-gallery__thumbs');
    if (!track || !slides.length) return;

    let activeIndex = 0;
    let scrollFrame;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const updateActive = (index) => {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1));
      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === activeIndex;
        thumb.setAttribute('aria-current', String(isActive));
        thumb.setAttribute('aria-pressed', String(isActive));
        thumb.tabIndex = isActive ? 0 : -1;
      });
      const activeThumb = thumbs[activeIndex];
      if (activeThumb && thumbTrack) {
        const left = activeThumb.offsetLeft - ((thumbTrack.clientWidth - activeThumb.offsetWidth) / 2);
        thumbTrack.scrollTo({ left: Math.max(0, left), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    };

    const goTo = (index, focusThumb = false) => {
      const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
      const slide = slides[nextIndex];
      track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      updateActive(nextIndex);
      if (focusThumb) thumbs[nextIndex]?.focus();
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => goTo(index));
      thumb.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        goTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
      });
    });

    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const trackLeft = track.getBoundingClientRect().left;
        const nextIndex = slides.reduce((closestIndex, slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          const closestDistance = Math.abs(slides[closestIndex].getBoundingClientRect().left - trackLeft);
          return distance < closestDistance ? index : closestIndex;
        }, 0);
        if (nextIndex !== activeIndex) updateActive(nextIndex);
      });
    }, { passive: true });

    updateActive(0);
  });

  document.querySelectorAll('[data-quantity-change]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = button.parentElement.querySelector('input');
      input.value = Math.max(1, Number(input.value || 1) + Number(button.dataset.quantityChange));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  document.querySelectorAll('[data-recommendations-url]').forEach(async (section) => {
    const response = await fetch(section.dataset.recommendationsUrl);
    if (!response.ok) return;
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = parsed.querySelector('[data-product-recommendations]');
    if (next?.querySelector('.product-card')) section.replaceWith(next);
  });

  document.querySelector('[data-discount-apply]')?.addEventListener('click', async (event) => {
    const button = event.currentTarget;
    const code = document.querySelector('[data-discount-input]')?.value;
    button.disabled = true;
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    });
    if (response.ok) window.location.reload();
    else button.disabled = false;
  });

  const timelineObservers = new WeakMap();
  const revealTimeline = (timeline) => {
    if (!timeline || timeline.classList.contains('is-revealed')) return;
    timeline.classList.add('is-revealed');
    timelineObservers.get(timeline)?.disconnect();
  };

  const initTimelineReveals = (scope = document) => {
    const timelines = [];
    if (scope.matches?.('[data-timeline-reveal]')) timelines.push(scope);
    scope.querySelectorAll?.('[data-timeline-reveal]').forEach((timeline) => timelines.push(timeline));

    timelines.forEach((timeline) => {
      if (timeline.dataset.timelineInitialized === 'true') return;
      timeline.dataset.timelineInitialized = 'true';

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        revealTimeline(timeline);
        return;
      }

      timeline.classList.add('is-reveal-ready');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) revealTimeline(entry.target);
        });
      }, { threshold: [0.2] });
      timelineObservers.set(timeline, observer);
      observer.observe(timeline);
    });
  };

  initTimelineReveals();
  document.addEventListener('shopify:section:load', (event) => initTimelineReveals(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-timeline-reveal]').forEach((timeline) => timelineObservers.get(timeline)?.disconnect());
  });
  document.addEventListener('shopify:section:select', (event) => revealTimeline(event.target.querySelector?.('[data-timeline-reveal]')));
  document.addEventListener('shopify:block:select', (event) => revealTimeline(event.target.closest?.('[data-timeline-reveal]')));
})();
